import React from 'react';
import moment from 'moment';
import { Translation } from 'react-i18next';
import SafeHTML from '../components/shared/SafeHTML';
import ResourceModel from '../models/Resource';
import ClinicalNote from '../models/ClinicalNote';
import PatientVisit from '../models/PatientVisit';
import ImmunizationResource from '../models/Immunization';
import ServiceRequest from '../models/ServiceRequest';
import AggregatedResource from '../models/Aggregated';
import type { ResourceType, Resource } from '../types/resource';
import { backendDateFormats } from './format';
import { generateKey, isHTMLJunk, compare } from './utils';

export function sortResourcesByField(resources, field, reverseOrder: bool) {
  if (reverseOrder) {
    return resources.sort((a, b) => compare(b, a, field));
  }
  return resources.sort((a, b) => compare(a, b, field));
}

export function getDateRangeForAggregatedResources(resources) {
  const minDate = resources.reduce((min, b) =>
    Math.min(min, moment(b.administeredAt)), moment(resources[0].administeredAt));
  const maxDate = resources.reduce((max, b) =>
    Math.max(max, moment(b.administeredAt)), moment(resources[0].administeredAt));
  return minDate === maxDate
    ? moment(minDate).format('ll')
    : `${moment(minDate).format('ll')} - ${moment(maxDate).format('ll')}`;
}

export const translateResourceType = (type: string, caseType = 'title') => {
  return (
    <Translation
      key={`resource_type_${generateKey()}`}
      ns='resourceTypes'
    >
      {(t) => (t(`${caseType}.${type}`))}
    </Translation>
  );
};

export const translateResourceTitle = (resourceType: string, properties: Object, isDefaultTitle: boolean = true) => {
  const translationFields = {
    ...properties
  };
  const parent = isDefaultTitle ? 'default' : 'nondefault';
  return (
    <Translation ns='resourceTitles'>
      {(resourceTitleT) => {
        return (
          <Translation
            key={`resource_type_${generateKey()}`}
            ns='resourceTypes'
          >
            {(resourceTypeT) => {
              translationFields.resourceType = resourceTypeT(`sentence.${resourceType}`);
              return resourceTitleT(`${parent}.${resourceType}`, translationFields);
            }}
          </Translation>
        );
      }}
    </Translation>
  );
};

const translateResourceProperty = (property: string, resourceType: string) => {
  return (
    <Translation
      ns={`${resourceType}Properties`}
    >
      {(resourceTypeT, { i18n }) => {
        if (!i18n.exists(`${resourceType}Properties:${property}`)) {
          // Look up translation in the file for common properties
          // (by looking up the child property name directly)
          const childPropertyIdx = property.lastIndexOf('.');
          const childProperty = childPropertyIdx > 0 ? property.substring(childPropertyIdx + 1) : property;
          return (
            <Translation ns='commonResourceProperties'>
              {(commonT) => commonT(childProperty)}
            </Translation>
          );
        }
        return resourceTypeT(property);
      }}
    </Translation>
  );
};

export const resourceTypes = {
  allergy: { color: 'persian-rose', name: translateResourceType('allergy') },
  carePlan: { color: 'shamrock', name: translateResourceType('carePlan') },
  clinicalNote: { color: 'purple', name: translateResourceType('clinicalNote') },
  communication: { color: 'disco-blue', name: translateResourceType('communication') },
  condition: { color: 'orange', name: translateResourceType('condition') },
  diagnosticReport: { color: 'lavender', name: translateResourceType('diagnosticReport') },
  familyMemberHistory: { color: 'forest-green', name: translateResourceType('familyMemberHistory') },
  form: { color: 'orange', name: translateResourceType('form') },
  goal: { color: 'neon-blue', name: translateResourceType('goal') },
  imagingStudy: { color: 'neon-purple', name: translateResourceType('imagingStudy') },
  immunization: { color: 'mandy', name: translateResourceType('immunization') },
  medicationDispense: { color: 'turquoise-blue', name: translateResourceType('medicationDispense') },
  medicationRequest: { color: 'rose', name: translateResourceType('medicationRequest') },
  observation: { color: 'neon-purple', name: translateResourceType('observation') },
  procedure: { color: 'turquoise', name: translateResourceType('procedure') },
  resourceGroup: { color: 'disco-blue', name: translateResourceType('resourceGroup') },
  patientVisit: { color: 'neon-blue', name: translateResourceType('patientVisit') },
  serviceRequest: { color: 'yellow', name: translateResourceType('serviceRequest') },
  default: { color: 'ship-cove', name: translateResourceType('default') }
};

export function createResourceModel(resource): ResourceModel {
  switch (resource.resource_type) {
  case 'immunization':
    return new ImmunizationResource(resource);
  case 'clinicalNote':
    return new ClinicalNote(resource);
  case 'patientVisit':
    return new PatientVisit(resource);
  case 'serviceRequest':
    return new ServiceRequest(resource);
  default:
    if (Array.isArray(resource) && resource[0].resource_type) {
      if ( // check if all resources are of same resource type
        resource.map((r) => r.resource_type)
          .every((rType) => rType === resource[0].resource_type)
      ) {
        return new AggregatedResource(resource);
      }
      // situation where have array of resource of different types? Groups?
      // return new GroupedResource(resource);
    }
    return new ResourceModel(resource);
  }
}

function isPrimitiveType(type) {
  return type === 'string'
    || type === 'number'
    || (Array.isArray(type) && (type.includes('string') || type.includes('number')));
}

export function parseModels(items: Resource[]): ResourceModel[] {
  // const aggregateTypes = ['immunization'];
  const aggregateTypes = [];
  const resourceModels: ResourceModel[] = [];
  const resourcesToAggregate: { [ResourceType]: Resource[] } = {};

  items.forEach((r) => {
    if (aggregateTypes.indexOf(r.resource_type) > -1) {
      resourcesToAggregate[r.resource_type] = resourcesToAggregate[r.resource_type] || [];
      resourcesToAggregate[r.resource_type].push(r);
    } else {
      resourceModels.push(createResourceModel(r));
    }
  });
  const aggregateResourceModels: AggregatedResource[] = Object.keys(resourcesToAggregate).map(
    (key) => createResourceModel(resourcesToAggregate[key])
  );
  return resourceModels.concat(aggregateResourceModels);
}

export function isModel(resource): boolean {
  return resource instanceof ResourceModel;
}

export const TEMPLATES = {
  allergy: ({ substance, type, recordedAt }) => {
    // accommodate old spelling mistake in some of our resources
    const allergyType = type && type.text && type.text.toLowerCase();
    const fixedAllergyType = (allergyType === 'intollerence') || (allergyType === 'intolerance')
      ? 'intolerance' : 'allergy';
    const substanceText = substance && substance.text;
    return {
      title: substanceText ?
        translateResourceTitle(fixedAllergyType, { substanceText, resourceType: fixedAllergyType }, false)
        : translateResourceType('allergyRecord', 'sentence'),
      date: recordedAt
    };
  },
  carePlan: ({ title, recordedAt }) => ({
    title: title || translateResourceTitle(
      'carePlan',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  clinicalNote: ({ subject, recordedAt }) => ({
    title: subject || translateResourceTitle(
      'clinicalNote',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  patientVisit: ({ recordedAt }) => ({
    title: translateResourceTitle(
      'patientVisit',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  communication: ({
    subject, sender, recipient, receivedAt, sentAt, recordedAt
  }) => {
    const date = receivedAt || sentAt || recordedAt;
    return {
      title: subject || ((sender && recipient) && `${sender} to ${recipient}`) || translateResourceTitle(
        'communication',
        { date: moment(date).format('L') }
      ),
      date
    };
  },
  condition: ({ name, recordedAt }) => ({
    title: name || translateResourceTitle(
      'condition',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  diagnosticReport: ({ name, recordedAt }) => ({
    title: (name && name.text) || translateResourceTitle(
      'diagnosticReport',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  familyMemberHistory: ({ name, recordedAt }) => ({
    title: name ? translateResourceTitle('familyHistory', { name }, false)
      : translateResourceTitle(
        'familyHistory',
        { date: moment(recordedAt).format('L') }
      ),
    date: recordedAt
  }),
  form: ({ name, recordedAt }) => ({
    title: name || translateResourceTitle(
      'form',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  goal: ({ target, recordedAt }) => ({
    title: (target && translateResourceTitle('goal', { target }, false)) || translateResourceTitle(
      'goal',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  imagingStudy: ({ name, recordedAt }) => ({
    title: (name && name.text) || translateResourceTitle(
      'imagingStudy',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  immunization: ({ name, recordedAt, drugName }) => ({
    title: (name && name.text) || drugName || translateResourceTitle(
      'immunization',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  medicationDispense: ({ name, dispensedAt }) => ({
    title: (name && name.text) || translateResourceTitle(
      'dispensedMedication',
      { date: moment(dispensedAt).format('L') }
    ),
    date: dispensedAt
  }),
  medicationRequest: ({ name, genericName, requestedAt }) => ({
    title: (name && name.text) || (genericName && genericName.text) || translateResourceTitle(
      'medicationRequest',
      { date: moment(requestedAt).format('L') }
    ),
    date: requestedAt
  }),
  observation: ({ name, recordedAt }) => ({
    title: (name && name.text) || translateResourceTitle(
      'observation',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  procedure: ({ name, recordedAt }) => ({
    title: name || translateResourceTitle(
      'procedure',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  resourceGroup: ({ name, recordedAt }) => ({
    title: name || translateResourceTitle(
      'resourceGroup',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  }),
  serviceRequest: ({ requestedService, recordedAt }) => ({
    title: (requestedService && requestedService.service) || translateResourceTitle(
      'serviceRequest',
      { date: moment(recordedAt).format('L') }
    ),
    date: recordedAt
  })
};

type ValuesToHTMLProps = {
  resourceBody: Object,
  parentProperty?: string,
  resourceType: string
};

// Recursively go through each property in the resource body and style it
// as HTML
export const valuesToHTML = ({ resourceBody, parentProperty, resourceType }: ValuesToHTMLProps) => {
  if (!resourceBody || !Object.keys(resourceBody).length) {
    return '';
  }
  const tree = Object.keys(resourceBody).reduce((acc, key) => {
    const translationKey = parentProperty ? `${parentProperty}.${key}` : key;
    const name = translateResourceProperty(translationKey, resourceType);
    let value = resourceBody[key];
    if (!value) return acc;

    const type = Array.isArray(value) ? 'array' : typeof value;

    if (type === 'object') {
      if (value.display) {
        return (
          <React.Fragment>
            {acc}
            <li>
              <strong>{name}:</strong>
              {value.display}
            </li>
          </React.Fragment>
        );
      }

      const childProperties = { ...value };
      const text = value.text || '';
      delete childProperties.text; // rendered separately before child properties

      const parentProperties = parentProperty ? `${parentProperty}.${key}Children` : `${key}Children`;
      const children = valuesToHTML({
        resourceBody: childProperties, parentProperty: parentProperties, resourceType
      });

      if (!children && !text) return acc;

      return (
        <React.Fragment>
          {acc}
          <li>
            <strong>{name}:</strong>
            {text}{children}
          </li>
        </React.Fragment>
      );
    }
    if (type === 'array') {
      if (value && value.length > 0) {
        let children = value.map((v) => {
          if (v.display) {
            return (
              <li key={`item_${generateKey()}`}>
                {v.display}
              </li>
            );
          }
          const parentProperties = parentProperty ? `${parentProperty}.${key}Children` : `${key}Children`;
          const item = valuesToHTML({ resourceBody: v, parentProperty: parentProperties, resourceType });
          if (!item) return '';
          return (
            <li
              key={`item_${generateKey()}`}
            >
              {item}
            </li>
          );
        });
        children = children.filter((c) => c !== '');
        if (!children.length) return acc;

        return (
          <React.Fragment>
            {acc}
            <li>
              <strong>{name}:</strong>
              <ul>
                {children}
              </ul>
            </li>
          </React.Fragment>
        );
      }
    }
    if (isPrimitiveType(type)) {
      // handle case that string is a date
      const dateTime = moment(value, 'lll', true);
      if (!dateTime.isValid()) { // 'lll' means it is already formatted, so leave as is if it matched
        const date = moment.parseZone(value, backendDateFormats, true);
        if (date.isValid()) {
          value = date.format('L');
        }
      }

      if (!value || !value.length || isHTMLJunk(value)) return acc;
      return (
        <React.Fragment>
          {acc}
          <li>
            <strong>{name}:</strong>
            {/* The value itself, though a string, might be in HTML, so try to render it in case */}
            {type === 'string' ? <SafeHTML text={value} /> : value}
          </li>
        </React.Fragment>
      );
    }
    // handle case that string is a boolean
    if (value === true || value === false || value === 'true' || value === 'false') {
      const isTrue = value === 'true' || value === true;
      return (
        <React.Fragment>
          {acc}
          <li>
            <strong>{name}:</strong>
            <Translation
              ns='common'
            >
              {(t, _, ready) => {
                if (!ready) {
                  return isTrue ? 'Yes' : 'No';
                }
                return t(`${isTrue ? 'Yes' : 'No'}`);
              }}
            </Translation>
          </li>
        </React.Fragment>
      );
    }
    return acc;
  }, '');

  if (tree) {
    return (
      <React.Fragment>
        <ul>
          {tree}
        </ul>
      </React.Fragment>
    );
  }
  return '';
};

export default resourceTypes;


// WEBPACK FOOTER //
// ./src/utils/health-resources.js